<template>
  <v-list-item v-bind="$attrs" active-class="active-item">
    <template v-slot:prepend="{ isActive }">
      <v-badge
        :content="countBadge"
        :model-value="!!countBadge"
        class="custom-badge"
        color="red"
        location="bottom right"
        :offset-x="offset"
        overlap
      >
        <slot name="icon">
          <v-icon class="item-icon" :color="iconColor">
            {{ isActive ? filledIcon : icon }}
          </v-icon>
        </slot>
      </v-badge>
    </template>
    <v-list-item-title class="item-content">
      {{ label }}
    </v-list-item-title>
  </v-list-item>
</template>

<script setup lang="ts">
export interface Props {
  countBadge?: number
  icon?: string
  iconColor?: string
  label: string
}

const props = defineProps<Props>()

const offset = computed(() => (props.countBadge && props.countBadge > 99 ? 2 : -2))
const filledIcon = computed(() => props.icon?.replace(/^mso:/, 'ms:'))
</script>

<style lang="scss" scoped>
$active-color: rgb(var(--v-theme-primary));
$base-color: rgb(var(--v-theme-mid-grey));
$hover-color: rgb(var(--v-theme-interactive));
$hover-background: rgb(var(--v-theme-interactive-lighten-1));

.v-list-item {
  border-radius: 0;
  padding-block: 0;
  padding-inline: 16px;
}

.child-item .item-content {
  font-weight: normal;
}

.child-item,
.item-content,
.item-icon {
  color: $base-color;
}

.item-content {
  font-size: 0.875rem;
}

.active-item,
.active-item .item-content,
.active-item .item-icon {
  color: $active-color;
}

// kills Vuetify's own activated-state tint (currentColor, i.e. orange, at a fixed low opacity)
// so active items never show a background — matches mobile
.active-item {
  --v-theme-overlay-multiplier: 0;
}

// active items opt out entirely so hovering one causes no visual change
.v-list-item:hover:not(.active-item) {
  background-color: $hover-background;
}

.v-list-item:hover:not(.active-item) .item-content,
.v-list-item:hover:not(.active-item) .item-icon {
  color: $hover-color;
}

:deep(.v-list-item__prepend) {
  --v-list-prepend-gap: 16px;
}

// beats Vuetify's own hover/active overlay tint (0,3,0 specificity) so our flat background above wins
:deep(.v-list-item.v-list-item .v-list-item__overlay) {
  opacity: 0;
}
</style>

<style lang="scss">
.custom-badge .v-badge__badge {
  padding: 2px 4px;
}
</style>
